'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';

import type { ElementFormatType } from 'lexical';

import type { EditorLabels } from '../Editor.types';

export const MIN_ALLOWED_FONT_SIZE = 8;
export const MAX_ALLOWED_FONT_SIZE = 72;
export const DEFAULT_FONT_SIZE = 15;

const DEFAULT_BLOCK_TYPE_NAMES = {
  bullet: 'Bulleted list',
  code: 'Code block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered list',
  paragraph: 'Normal',
  quote: 'Quote',
} as const;

export const getBlockTypeToBlockName = (labels?: EditorLabels) => ({
  bullet: labels?.bulletedList ?? DEFAULT_BLOCK_TYPE_NAMES.bullet,
  code: labels?.codeBlock ?? DEFAULT_BLOCK_TYPE_NAMES.code,
  h1: labels?.heading1 ?? DEFAULT_BLOCK_TYPE_NAMES.h1,
  h2: labels?.heading2 ?? DEFAULT_BLOCK_TYPE_NAMES.h2,
  h3: labels?.heading3 ?? DEFAULT_BLOCK_TYPE_NAMES.h3,
  h4: labels?.heading4 ?? DEFAULT_BLOCK_TYPE_NAMES.h4,
  h5: labels?.heading5 ?? DEFAULT_BLOCK_TYPE_NAMES.h5,
  h6: labels?.heading6 ?? DEFAULT_BLOCK_TYPE_NAMES.h6,
  number: labels?.numberedList ?? DEFAULT_BLOCK_TYPE_NAMES.number,
  paragraph: labels?.normal ?? DEFAULT_BLOCK_TYPE_NAMES.paragraph,
  quote: labels?.quote ?? DEFAULT_BLOCK_TYPE_NAMES.quote,
});

// Export default for backward compatibility
export const blockTypeToBlockName = DEFAULT_BLOCK_TYPE_NAMES;

const INITIAL_TOOLBAR_STATE = {
  blockType: 'paragraph' as keyof typeof blockTypeToBlockName,
  elementFormat: 'left' as ElementFormatType,
  // Current font size in px
  fontSize: `${DEFAULT_FONT_SIZE}px`,
  // Font size input value - for controlled input
  fontSizeInputValue: `${DEFAULT_FONT_SIZE}`,
  isBold: false,
  isItalic: false,
  isRTL: false,
  isStrikethrough: false,
  isSubscript: false,
  isSuperscript: false,
  isUnderline: false,
  isLowercase: false,
  isUppercase: false,
  isCapitalize: false,
};

type ToolbarState = typeof INITIAL_TOOLBAR_STATE;

// Utility type to get keys and infer value types
type ToolbarStateKey = keyof ToolbarState;
type ToolbarStateValue<Key extends ToolbarStateKey> = ToolbarState[Key];

type ContextShape = {
  toolbarState: ToolbarState;
  updateToolbarState<Key extends ToolbarStateKey>(
    key: Key,
    value: ToolbarStateValue<Key>,
  ): void;
  labels?: EditorLabels;
  disabled?: boolean;
};

const Context = createContext<ContextShape | undefined>(undefined);

export const ToolbarContext = ({
  children,
  labels,
  disabled = false,
}: {
  children: ReactNode;
  labels?: EditorLabels;
  disabled?: boolean;
}): JSX.Element => {
  const [toolbarState, setToolbarState] = useState(INITIAL_TOOLBAR_STATE);

  const updateToolbarState = useCallback(
    <Key extends ToolbarStateKey>(key: Key, value: ToolbarStateValue<Key>) => {
      setToolbarState((prev) => {
        const newState = {
          ...prev,
          [key]: value,
        };
        // Update fontSizeInputValue when fontSize changes
        if (key === 'fontSize') {
          newState.fontSizeInputValue = (value as string).slice(0, -2);
        }
        return newState;
      });
    },
    [],
  );

  const contextValue = useMemo(() => {
    return {
      toolbarState,
      updateToolbarState,
      labels,
      disabled,
    };
  }, [toolbarState, updateToolbarState, labels, disabled]);

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export const useToolbarState = () => {
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error('useToolbarState must be used within a ToolbarProvider');
  }

  return context;
};
